import { useState } from "react";
import { Download, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/shared/PageHeader";
import { FormInput } from "@/components/shared/FormInput";
import { PrimaryButton } from "@/components/shared/Buttons";
import { EmptyState } from "@/components/shared/States";
import { useSeo } from "@/hooks/useSeo";
import { getHistory, receiptUrl, isValidIndianMobile, isValidEmail } from "@/lib/paymentApi";

const badge = (s) => {
  const map = { Successful: "bg-emerald-100 text-emerald-700", Failed: "bg-red-100 text-red-600", Created: "bg-slate-100 text-slate-600", Pending: "bg-amber-100 text-amber-700", Refunded: "bg-blue-100 text-blue-700", "Partially Refunded": "bg-blue-100 text-blue-700" };
  return map[s] || "bg-slate-100 text-slate-600";
};

export default function PaymentHistory() {
  useSeo("Payment History", "View your ASAI payment history and download receipts.");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = async (e) => {
    e.preventDefault();
    if (!isValidEmail(email) || !isValidIndianMobile(mobile)) {
      toast.error("Enter your registered email and mobile number.");
      return;
    }
    setLoading(true);
    try {
      setItems(await getHistory(email.trim(), mobile.trim()));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-payment-history">
      <PageHeader overline="Student" titleLines={["Your payment", "history"]} description="Enter your registered email and mobile number to view your payments and download receipts." />

      <section className="bg-white py-20 md:py-28">
        <div className="container-asai max-w-3xl">
          <form onSubmit={search} noValidate data-testid="history-form" className="mb-10 flex flex-col gap-4 rounded-2xl border border-navy/10 bg-lightgrey p-6 sm:flex-row sm:items-end">
            <FormInput label="Email" id="h-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="flex-1" testId="history-email" />
            <FormInput label="Mobile" id="h-mobile" value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="10-digit mobile" className="flex-1" testId="history-mobile" />
            <PrimaryButton type="submit" size="lg" disabled={loading} testId="history-search">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Search
            </PrimaryButton>
          </form>

          {items === null ? null : items.length === 0 ? (
            <EmptyState title="No payments found" description="We couldn't find payments for those details. Please check and try again." />
          ) : (
            <div className="overflow-hidden rounded-2xl border border-navy/10" data-testid="history-results">
              <table className="w-full text-left text-sm">
                <thead className="bg-lightgrey text-xs uppercase tracking-wide text-navy/50">
                  <tr>
                    <th className="px-4 py-3">Purpose</th><th className="px-4 py-3">Amount</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Receipt</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((p) => (
                    <tr key={p.paymentRef} className="border-t border-navy/10">
                      <td className="px-4 py-3 text-navy">{p.purposeLabel}</td>
                      <td className="px-4 py-3 text-navy">₹{p.amountInr.toLocaleString("en-IN")}</td>
                      <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-1 text-xs font-medium ${badge(p.status)}`}>{p.status}</span></td>
                      <td className="px-4 py-3">
                        {p.status === "Successful" ? (
                          <a href={receiptUrl(p.paymentRef)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm font-medium text-royal hover:text-orange">
                            <Download className="h-4 w-4" /> Receipt
                          </a>
                        ) : <span className="text-xs text-navy/40">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
